/**
 * Create / edit a Project. Single modal handles both flows: pass a `project`
 * to edit it, or omit for create.
 */

import { useEffect, useState } from 'react';
import {
  useCreateProject,
  useUpdateProject,
  type ProjectDto,
  type ProjectStatus,
} from '../api/projects';
import { useContexts } from '../api/catalog';
import { FormModal } from './FormModal';
import styles from './FormModal.module.css';

interface ProjectFormModalProps {
  open: boolean;
  onClose: () => void;
  project?: ProjectDto | null;
}

const STATUS_OPTIONS: { id: ProjectStatus; label: string }[] = [
  { id: 'on_track', label: 'On track' },
  { id: 'needs_attention', label: 'Needs attention' },
  { id: 'idle', label: 'Idle' },
  { id: 'blocked', label: 'Blocked' },
];

export function ProjectFormModal({ open, onClose, project }: ProjectFormModalProps) {
  const isEdit = !!project;
  const create = useCreateProject();
  const update = useUpdateProject();
  const contextsQ = useContexts();
  const contexts = contextsQ.data?.contexts ?? [];

  const [name, setName] = useState('');
  const [outcome, setOutcome] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('on_track');
  const [contextId, setContextId] = useState<string | null>(null);
  const [targetDate, setTargetDate] = useState<string>('');

  // Reset when opening / changing target.
  useEffect(() => {
    if (!open) return;
    setName(project?.name ?? '');
    setOutcome(project?.outcome ?? '');
    setStatus((project?.status as ProjectStatus | undefined) ?? 'on_track');
    setContextId(project?.contextId ?? null);
    setTargetDate(project?.targetDate ?? '');
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id]);

  const pending = create.isPending || update.isPending;
  const valid = name.trim().length > 0 && outcome.trim().length > 0;
  const error =
    (create.error as Error | null)?.message ??
    (update.error as Error | null)?.message ??
    null;

  const submit = () => {
    if (!valid || pending) return;
    const payload = {
      name: name.trim(),
      outcome: outcome.trim(),
      status,
      contextId,
      targetDate: targetDate || null,
    };
    if (isEdit && project) {
      update.mutate(
        { id: project.id, input: payload },
        { onSuccess: () => onClose() },
      );
    } else {
      create.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit project' : 'New project'}
      subtitle={isEdit ? undefined : 'A project is an outcome with a target date. Tasks roll up under it.'}
      submitLabel={isEdit ? 'Save changes' : 'Create project'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
    >
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="proj-name">Name</label>
        <input
          id="proj-name"
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Future Life launch"
          maxLength={120}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="proj-outcome">Outcome</label>
        <textarea
          id="proj-outcome"
          className={styles.textarea}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          placeholder="What does done look like? One sentence."
          maxLength={500}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Status</span>
        <div className={styles.pills}>
          {STATUS_OPTIONS.map((o) => (
            <button
              type="button"
              key={o.id}
              className={`${styles.pill} ${status === o.id ? styles.pillActive : ''}`}
              onClick={() => setStatus(o.id)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <span className={styles.fieldLabel}>Context</span>
        <div className={styles.pills}>
          <button
            type="button"
            className={`${styles.pill} ${contextId === null ? styles.pillActive : ''}`}
            onClick={() => setContextId(null)}
          >
            None
          </button>
          {contexts.map((ctx) => (
            <button
              type="button"
              key={ctx.id}
              className={`${styles.pill} ${contextId === ctx.id ? styles.pillActive : ''}`}
              onClick={() => setContextId(ctx.id)}
            >
              <span className={styles.pillDot} style={{ background: ctx.color }} />
              {ctx.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="proj-target">Target date (optional)</label>
        <input
          id="proj-target"
          className={styles.input}
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
        />
      </div>
    </FormModal>
  );
}
